<template>
  <div class="flex py-5">
    <div class="content-center mr-2">
      <button v-if="showPrev" class="!min-w-0 btn btn-md btn-flat link flex" @click="prev">
        <img class="w-3 min-w-3 mr-1 scale-x-[-1]" src="@/assets/icons/arrow-right.svg" alt="prev" />
        <span v-if="showBtnLabel" class="text-base-bold">{{ prevBtnLabel ?? 'Previous' }}</span>
      </button>
    </div>
    <ol class="gap-x-1 flex mx-auto">
      <li v-for="(item, index) of visiblePages" :key="index">
        <button
          v-if="item.type === 'page'"
          :data-test-id="'page_' + item.value"
          :class="[
            'text-base-bold link btn btn-md btn-flat !min-w-0 !py-2 !px-4',
            page == item.value && '!bg-racq-white',
          ]"
          @click="
            updatePage(item.value!);
            focus(item.value!);
          "
        >
          {{ item.value }}
        </button>
        <span v-else class="text-base-bold text-racq-blue !py-2 flex items-center">..</span>
      </li>
    </ol>
    <div class="content-center ml-2">
      <button v-if="showNext" class="!min-w-0 btn btn-md btn-flat link flex ml-auto" @click="next">
        <span v-if="showBtnLabel" class="text-base-bold">{{ nextBtnLabel ?? 'Next' }}</span>
        <img class="w-3 min-w-3 ml-1" src="@/assets/icons/arrow-right.svg" alt="next" />
      </button>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { getScreenSize } from '@/utils/dom-helpers';
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps<{
  total: number;
  itemsPerPage: number;
  defaultPage?: number;
  nextBtnLabel?: string;
  prevBtnLabel?: string;
}>();

const page = ref(props.defaultPage ?? 1);
watch(
  () => props.defaultPage,
  (newValue) => (page.value = newValue ?? 1)
);

const emit = defineEmits(['prev', 'next', 'updatePage']);

const numOfPages = computed(() => {
  return Math.ceil(props.total / props.itemsPerPage);
});
const maxVisiblePages = computed(() => {
  const sizes = getScreenSize();
  if (sizes.md.value) {
    return 5;
  } else if (sizes.sm.value || sizes.xse.value) {
    return 4;
  }
  return 7;
});
const showBtnLabel = computed(() => {
  return maxVisiblePages.value == 7;
});

const visiblePages = computed(() => {
  const totalPages = numOfPages.value;

  // If total pages fit within max visible pages, show all pages
  if (totalPages <= maxVisiblePages.value) {
    return new Array(totalPages).fill(0).map((_, index) => ({
      type: 'page',
      value: index + 1,
    }));
  }

  const currentPage = page.value;
  // Initialize items array with first page (always visible)
  const items: Array<{ type: 'page' | 'ellipsis'; value?: number }> = [{ type: 'page', value: 1 }];

  // Calculate how many pages to show before and after current page
  // Reserve space for first page (1) and last page, so subtract 2 from max visible
  let numbersBeforeCurrentPage,
    numbersAfterCurrentPage = (numbersBeforeCurrentPage = Math.floor((maxVisiblePages.value - 2) / 2));

  // Special case for even max visible pages
  if (numbersAfterCurrentPage + numbersBeforeCurrentPage + 1 > maxVisiblePages.value - 2) {
    numbersBeforeCurrentPage -= 1;
  }

  // Adjust numbers if current page is near the end - shift more pages to the left
  if (currentPage + numbersAfterCurrentPage >= totalPages) {
    numbersBeforeCurrentPage += currentPage + numbersAfterCurrentPage - totalPages + 1;
  }

  // Iterate through pages 2 to totalPages-1 (first and last are handled separately)
  let i = 2;
  while (i < totalPages) {
    const prevItemIsNotEllipsis = items[items.length - 1]?.type != 'ellipsis';

    // If page is too far before current page, show ellipsis
    if (i < currentPage - numbersBeforeCurrentPage) {
      prevItemIsNotEllipsis && items.push({ type: 'ellipsis' });
    }
    // If we haven't reached max visible pages yet, add the page number
    else if (items.filter((item) => item.type == 'page').length < maxVisiblePages.value - 1) {
      items.push({ type: 'page', value: i });
    }
    // If page is too far after current page, show ellipsis
    else if (i > currentPage + numbersAfterCurrentPage) {
      prevItemIsNotEllipsis && items.push({ type: 'ellipsis' });
    }
    i++;
  }

  // Always show last page
  items.push({ type: 'page', value: totalPages });
  return items;
});

const showPrev = computed(() => page.value > 1);
const showNext = computed(() => page.value < numOfPages.value);

const prev = () => {
  page.value -= 1;
  emit('prev');
  emit('updatePage', page.value);
};

const next = () => {
  page.value += 1;
  emit('next');
  emit('updatePage', page.value);
};

const updatePage = (p: number) => {
  page.value = p;
  emit('updatePage', p);
};

const focus = (p: number) => {
  nextTick(() => (document.querySelector(`[data-test-id=page_${p}]`) as HTMLButtonElement)?.focus());
};
</script>
